<template>
  <div>
    <!-- Contenu principal de l'exposition -->
    <div v-if="!homePage" class="content">
      <h1 id="title">{{ name }}</h1>

      <!-- Boucle sur les ressources -->
      <div
        class="resources"
        v-for="(resource, index) in resources"
        :key="index"
      >
        <!-- IMAGE -->
        <template v-if="isImage(resource.name)">
          <img
            :src="resourceUrls[index]"
            loading="lazy"
            class="resource-media"
            data-aos="fade-up"
            data-aos-offset="200"
            :data-aos-delay="index * 150"
            data-aos-duration="800"
            data-aos-easing="ease-out-cubic"
            data-aos-anchor-placement="top-bottom"
            alt="Resource image"
            @load="refreshAOS"
          />
        </template>

        <!-- VIDEO -->
        <template v-else-if="isVideo(resource.name)">
          <video
            controls
            class="resource-media"
            data-aos="fade-up"
            data-aos-offset="200"
            :data-aos-delay="index * 150"
            data-aos-duration="800"
            @loadedmetadata="refreshAOS"
          >
            <source :src="resourceUrls[index]" :type="getVideoType(resource.name)" />
            Your browser does not support the video tag.
          </video>
        </template>

        <!-- AUDIO -->
        <template v-else-if="isAudio(resource.name)">
          <audio
            controls
            class="resource-media"
            data-aos="fade-up"
            data-aos-offset="200"
            :data-aos-delay="index * 150"
            data-aos-duration="800"
          >
            <source :src="resourceUrls[index]" type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </template>

        <!-- PDF -->
        <template v-else-if="isPDF(resource.name)">
          <embed
            :src="resourceUrls[index]"
            type="application/pdf"
            class="resource-media pdf-embed"
            data-aos="fade-up"
            data-aos-offset="200"
            :data-aos-delay="index * 150"
            data-aos-duration="800"
          />
        </template>
      </div>

      <!-- Footer -->
      <div class="footer">
        <div class="marquee-rtl">
          <div>Université de Corse</div>
        </div>
        <div class="credits">
          <span>Mentions Legales</span>
          <br />
          <span>Fattu in Corsica</span>
        </div>
      </div>
    </div>

    <!-- Page d'accueil -->
    <div class="home-container" v-else>
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="exhibitId"
        @exit="exitHomePage"
      ></component>
    </div>
  </div>
</template>

<script setup>
/* Le script est identique à votre version sauf qu’on peut changer 
   AOS.init({ once: false }) pour que ça rejoue si on scrolle vers le haut */
import { ref, onMounted, onBeforeMount, watch, defineProps, computed, nextTick } from 'vue';
import axios from 'axios';
import { useRoute } from "vue-router";

// AOS
import AOS from 'aos';
import 'aos/dist/aos.css';

// Modèles
import Modele_1 from '../../components/Models/Modele_1.vue';
import Modele_2 from '../../components/Models/Modele_2.vue';
import Modele_3 from '../../components/Models/Modele_3.vue';
import Modele_4 from '../../components/Models/Modele_4.vue';
import Modele_5 from '../../components/Models/Modele_5.vue';
import Modele_6 from '../../components/Models/Modele_6.vue';
import Modele_7 from '../../components/Models/Modele_7.vue';

const props = defineProps({
  exhibitId: String,
  homePageProps: Boolean,
});

const resources = ref([]);
const name = ref('');
const userName = ref('');
const homeImage = ref({});
const homePage = ref(props.homePageProps == false && props.exhibitId ? false : true);
const selectedTemplate = ref('');
const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);
const resourceUrls = ref([]);

const modeleComponents = {
  Modele_1,
  Modele_2,
  Modele_3,
  Modele_4,
  Modele_5,
  Modele_6,
  Modele_7,
};

const dynamicComponent = computed(() => {
  return modeleComponents[selectedTemplate.value] || null;
});

onBeforeMount(async () => {
  id.value = props.exhibitId ?? route.params.id;
  await getHomeImage();
  await getResources();
  await getExhibitName();
  await getUserName();
  await getModel();
});

onMounted(() => {
  AOS.init({
    once: false,
  });
  // En cas de contenu chargé après le mount
  setTimeout(() => {
    try { AOS.refreshHard(); } catch (e) { try { AOS.refresh(); } catch (_) { void 0; } }
  }, 300);
});

watch(
  resources,
  async (newResources) => {
    resourceUrls.value = await Promise.all(
      newResources.map(async (resource) => {
        const url = `${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`;
        const response = await axios.get(url);
        return response.data; 
      })
    );
    // S'assurer qu'AOS connaît les nouveaux éléments
    await nextTick();
    try {
      AOS.refreshHard();
    } catch (e) {
      try { AOS.refresh(); } catch (_) { void 0; }
    }
  },
  { immediate: true }
);

const getResources = async () => {
  try {
    let resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
    let response = await axios.post(resourcesUrl, { exhibitId: id.value });
    let allResources = response.data;

    let galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${id.value}`;
    let galleryResponse = await axios.get(galleryUrl);
    let galleryResourceIds = galleryResponse.data.map((res) => res.resource_id);

    resources.value = allResources.filter((resource) =>
      galleryResourceIds.includes(resource.id)
    );
  } catch (error) {
    console.error("Error fetching resources:", error);
  }
};

const getHomeImage = async () => {
  let url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`;
  try {
    let response = await axios.get(url);
    homeImage.value = response.data[0];
  } catch (error) {
    console.log(error);
  }
};

const getUserName = async () => {
  let url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
  let response = await axios.get(url);
  userName.value = response.data.organisation;
};

const getExhibitName = async () => {
  let url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
  let response = await axios.get(url);
  name.value = response.data.exhibit_name;
};

const getModel = async () => {
  let url = `${process.env.VUE_APP_BASEURL}/model/${id.value}`;
  try {
    let response = await axios.get(url);
    if (response.data.length > 0) {
      selectedTemplate.value = response.data[0].model;
      selectedTemplate.value = selectedTemplate.value
        .replace(/è/g, 'e')
        .replace(/\s+/g, '_');
    }
  } catch (error) {
    console.log(error);
  }
};

const exitHomePage = () => {
  homePage.value = false;
};

const isImage = (fileName) => /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
const isVideo = (fileName) => /\.(mp4|mov|avi|webm|mkv)$/i.test(fileName);
const isAudio = (fileName) => /\.(mp3|wav|ogg|m4a)$/i.test(fileName);
const isPDF = (fileName) => /\.pdf$/i.test(fileName);

const getVideoType = (fileName) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  switch (ext) {
    case 'mp4': return 'video/mp4';
    case 'webm': return 'video/webm';
    case 'mov': return 'video/quicktime';
    case 'avi': return 'video/x-msvideo';
    case 'mkv': return 'video/x-matroska';
    default: return '';
  }
};

const refreshAOS = () => {
  try { AOS.refreshHard(); } catch (e) { try { AOS.refresh(); } catch (_) { void 0; } }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Oswald:wght@300&display=swap");

body {
  background-color: #f5f0ee;
  margin: 0;
  padding: 0;
  font-family: "Oswald", sans-serif;
}

.content {
  background-color: #f5f0ee;
  min-height: 100vh;
  padding-bottom: 2rem;
}

#title {
  text-align: center;
  font-size: 3rem;
  margin: 1rem 0;
  text-transform: uppercase;
}

/* Chaque .resources aura min-height : 600px pour forcer de gros blocs */
.resources {
  margin: 3rem 0;
  text-align: center;
  min-height: 400px; /* espacement raisonnable pour déclencher AOS */
}

/* Média responsif et net */
.resource-media {
  width: min(100%, 1200px);
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  object-fit: contain;
  border-radius: 8px;
}

/* Audio: largeur raisonnable */
.resource-media[type="audio"] {
  width: min(100%, 800px);
}

/* PDF: garder une hauteur confortable sur desktop, responsive sur mobile */
.pdf-embed {
  width: min(100%, 900px);
  height: 70vh;
}

.footer {
  margin-top: 3rem;
  text-align: center;
}

.marquee-rtl {
  overflow: hidden;
  width: 100%;
  margin: 2em auto;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  text-transform: uppercase;
}

.marquee-rtl > div {
  display: inline-block;
  white-space: nowrap;
  animation: defilement-rtl 15s infinite linear;
  cursor: pointer;
  padding: 10px 2em 10px 100%;
  font-size: 1.2em;
}

@keyframes defilement-rtl {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

.credits {
  margin-top: 1rem;
}

.home-container {
  width: 100%;
  height: 100vh;
  background-color: #fff;
  position: relative;
}
</style>
